import type { ShareType } from 'src/utils/share';
import React from 'react';
import cn from 'classnames';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import feedBg from 'src/assets/feed-bg.png';
import Head from 'src/components/common/Head';
import Header from 'src/components/common/Header';
import Icon from 'src/components/common/Icon';
import LiquidGlassEffect from 'src/components/common/LiquidGlassEffect';
import VideoJSPlayer from 'src/components/common/VideoJSPlayer';
import { DOMAIN_URL, PRAYER_HUB_SHARE_DESCRIPTION, PRAYER_HUB_SHARE_TITLE, SHARE_CONSTANTS } from 'src/constants/other';
import { PRAYER_ROUTE, ROOT } from 'src/constants/routes';
import { useTranslation } from 'src/hooks/translations';
import { prayerHubs as prayerHubsApi } from 'src/store/api/prayer-hubs';
import { formatBucketLink } from 'src/utils/format-bucket-link';
import { removeHtmlTags } from 'src/utils/remove-html-tags';
import { share } from 'src/utils/share';

import css from './PrayerHub.module.scss';

const getMediaLink = (src: string) => {
  if (!src) return;

  return formatBucketLink(src);
};

export default function PrayerHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { translate: _t_createHub } = useTranslation('createHub');
  const { data } = prayerHubsApi.endpoints.getPrayerHub.useQuery({ id: params.id ?? '' });
  const prayerHubData = data?.success ? data.data : null;
  const videoSrc = prayerHubData?.video ? getMediaLink(prayerHubData?.video) : '';
  const imgSrc = prayerHubData?.image ? getMediaLink(prayerHubData?.image) : '';
  const seoDescription = prayerHubData ? removeHtmlTags(prayerHubData?.description as string) : '';

  const onShareClick = React.useCallback(() => {
    if (!data) return;

    share({
      type: SHARE_CONSTANTS.WHATS_APP as ShareType,
    });
  }, [data]);

  return (
    <div className={css.prayerHubWrapper} style={{ backgroundImage: `url(${feedBg})` }}>
      <Head
        seo={{
          title: prayerHubData?.personName,
          description: seoDescription,
        }}
        og={{
          title: PRAYER_HUB_SHARE_TITLE.replace(
            '${name}',
            `${prayerHubData?.personName} ${_t_createHub(`buttons.${prayerHubData?.gender}`)} ${prayerHubData?.motherName}`
          ),
          description: PRAYER_HUB_SHARE_DESCRIPTION,
        }}
        canonicalUrl={`${DOMAIN_URL}${location.pathname}${location.search}`}
      />
      <Header className={css.header} hideLogo />
      <LiquidGlassEffect className={css.backBtn} onClick={() => navigate(-1)}>
        חזרה
      </LiquidGlassEffect>
      {prayerHubData && (
        <div className={cn(css.prayerHub, !!videoSrc && css.videoPlayerActive, css.descriptionExpanded)}>
          <div className={css.mediaClip}>
            <div className={css.mediaFrame}>
              <div className={css.media}>
                {!videoSrc && imgSrc && (
                  <div
                    className={css.bg}
                    style={{
                      backgroundImage: `url(${imgSrc})`,
                    }}
                  />
                )}

                {videoSrc && (
                  <VideoJSPlayer
                    id={`campaign-video-${prayerHubData?._id}`}
                    className={css.videoPlayer}
                    videoSRC={videoSrc}
                    isDefaultControls
                    // unmutedInView
                  />
                )}

                <div className={css.actions}>
                  <button type="button" aria-label="שתף בוואטסאפ" onClick={onShareClick}>
                    <Icon type="share" />
                  </button>
                  {/* <button type="button">
                  <Icon type="bookmark" />
                </button> */}
                  {/* <button type="button" className={cn(prayerHubData.participationCount != null && css.prayerAction)}>
                    <Icon type="praying-hands" />
                    {prayerHubData.participationCount !== 0 && (
                      <span className={css.prayerCount}>{prayerHubData.participationCount}</span>
                    )}
                  </button> */}
                </div>
                <div className={css.info}>
                  <div className={css.titleWrap}>
                    <p
                      className={css.title}
                    >{`${prayerHubData.personName} ${_t_createHub(`buttons.${prayerHubData.gender}`)} ${prayerHubData.motherName}`}</p>
                  </div>
                  {/* <p
                    className={cn(css.descriptionPreview, isDescriptionExpanded && css.descriptionPreviewHidden)}
                    aria-hidden={isDescriptionExpanded}
                  >
                    <span className={css.descriptionText}>{prayerHubData.description}</span>
                  </p> */}
                </div>
              </div>
            </div>

            <div className={css.descriptionPanel}>
              <div className={css.descriptionPanelInner} data-feed-inner-scroll>
                <span className={css.category}>{prayerHubData.category?.title.he}</span>
                <p
                  className={css.expandedTitle}
                >{`${prayerHubData.personName} ${_t_createHub(`buttons.${prayerHubData.gender}`)} ${prayerHubData.motherName}`}</p>
                <p className={css.descriptionFull} dangerouslySetInnerHTML={{ __html: prayerHubData.description }}></p>
                <Link to={generatePath(PRAYER_ROUTE, { id: prayerHubData._id })} className={css.link}>
                  חזקו בתפילה
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
